import { AxiosRequestConfig } from 'axios'
import { Get } from './Get'
import { Post } from './Post'
import { Put } from './Put'
import { Delete } from './Delete'
import { ApiHelpers, APIHosts } from './ApiHelpers'
import { CreateProductDto, PermissionFormValues, UpdateProductDto } from './Product.interface'

class ProductServiceController extends ApiHelpers {
  ApiHost = APIHosts.LicenseManager;

  public Get = {
    getProducts: (options: AxiosRequestConfig = {}) => {
      const baseUrl = this.getHost(this.ApiHost);
      return Get(`${baseUrl}/products`, options);
    },
    getProductClaimsById: (guid: string, options: AxiosRequestConfig = {}) => {
      const baseUrl = this.getHost(this.ApiHost);
      return Get(`${baseUrl}/products/${guid}/claims`, options);
    },
    // license-manager/src/License/Controllers/PermissionGroupsController.cs GetAll — không phân trang
    // (số group của 1 Product luôn nhỏ, khác với số Permission có thể lên tới hàng nghìn).
    getPermissionGroups: (productId: string, options: AxiosRequestConfig = {}) => {
      const baseUrl = this.getHost(this.ApiHost);
      return Get(`${baseUrl}/products/${productId}/permission-groups`, options);
    },
    // license-manager/src/License/Controllers/PermissionsController.cs Search — search/group/page/pageSize,
    // đã phân trang + lọc ở DB (PermissionService.SearchAsync), Product Detail chỉ cần gọi lại đúng endpoint này.
    searchPermissions: (
      productId: string,
      params: { search?: string; group?: string; page?: number; pageSize?: number },
      options: AxiosRequestConfig = {},
    ) => {
      const baseUrl = this.getHost(this.ApiHost);
      return Get(`${baseUrl}/products/${productId}/permissions`, { ...options, params });
    },
  }

  public Post = {
    // license-manager/src/License/Controllers/ProductsController.cs Create — [Authorize(Roles = "System")]
    createProduct: (data: CreateProductDto, options: AxiosRequestConfig = {}) => {
      const baseUrl = this.getHost(this.ApiHost);
      return Post(`${baseUrl}/products`, data, options);
    },
    // POST /license_manager/products/{productId}/permissions/import — multipart/form-data, field "file".
    // Backend tự detect PermissionGroups/Permissions/Roles sheet, không parse Excel ở frontend.
    importPermissions: (
      productId: string,
      file: File,
      options: AxiosRequestConfig = {},
    ) => {
      const baseUrl = this.getHost(this.ApiHost);
      const formData = new FormData();
      formData.append('file', file);
      return Post(`${baseUrl}/products/${productId}/permissions/import`, formData, options);
    },
    // license-manager/src/License/Controllers/PermissionsController.cs Create.
    createPermission: (
      productId: string,
      data: PermissionFormValues,
      options: AxiosRequestConfig = {},
    ) => {
      const baseUrl = this.getHost(this.ApiHost);
      return Post(`${baseUrl}/products/${productId}/permissions`, data, options);
    },
  }

  public Put = {
    // license-manager/src/License/Controllers/ProductsController.cs Edit — chỉ Name thực sự được cập nhật server-side,
    // ClientId chỉ dùng để validate (xem ghi chú disabled ở ProductFormModal).
    updateProduct: (id: string, data: UpdateProductDto, options: AxiosRequestConfig = {}) => {
      const baseUrl = this.getHost(this.ApiHost);
      return Put(`${baseUrl}/products/${id}`, data, options);
    },
    // license-manager/src/License/Controllers/PermissionsController.cs Update — cùng body shape với Create.
    updatePermission: (
      productId: string,
      permissionId: string,
      data: PermissionFormValues,
      options: AxiosRequestConfig = {},
    ) => {
      const baseUrl = this.getHost(this.ApiHost);
      return Put(`${baseUrl}/products/${productId}/permissions/${permissionId}`, data, options);
    },
  }

  public Delete = {
    // license-manager/src/License/Controllers/PermissionsController.cs Delete.
    deletePermission: (
      productId: string,
      permissionId: string,
      options: AxiosRequestConfig = {},
    ) => {
      const baseUrl = this.getHost(this.ApiHost);
      return Delete(`${baseUrl}/products/${productId}/permissions/${permissionId}`, {}, options);
    },
    // license-manager/src/License/Controllers/PermissionsController.cs BulkDelete — 1 request, không phải
    // N request cho N permission (all-or-nothing ở backend).
    bulkDeletePermissions: (
      productId: string,
      permissionIds: string[],
      options: AxiosRequestConfig = {},
    ) => {
      const baseUrl = this.getHost(this.ApiHost);
      return Delete(`${baseUrl}/products/${productId}/permissions`, permissionIds, options);
    },
  }
}

export const ProductService = new ProductServiceController()
